import React, { useCallback, useState } from 'react';
import SRCards from '../../components/SRCards';
import {
    Button, Container, Dialog, DialogTitle, DialogContent, DialogActions,
    Snackbar, TextField, CircularProgress, Box, Paper, List, ListItem,
    ListItemText, Chip, Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import SelectCompany from '../../components/SelectCompany';
import UserApi from '../../services/user.service';
import CommonServiceApi from '../../services/common-service';

export default function ForceLogout() {
    const [selectedCompany, setSelectedCompany] = useState<any>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [searching, setSearching] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
    const [dialog, setDialog] = useState(false);
    const [snackBar, setSnackBar] = useState('');
    const [pageResponse, setPageResponse] = useState('');
    const [errorDialog, setErrorDialog] = useState(false);

    const apiservices = new CommonServiceApi();

    const onCompanySelect = (company: any) => {
        setSelectedCompany(company);
        setSelectedUsers([]);
        setSearchQuery('');
        setSearchResults([]);
    };

    const handleSearchChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchQuery(value);

        if (value.trim().length < 2) {
            setSearchResults([]);
            return;
        }
        if (!selectedCompany || !selectedCompany.value) {
            return;
        }
        try {
            setSearching(true);
            const result = await apiservices.searchUsers(value);
            setSearchResults(Array.isArray(result) ? result : []);
        } catch (err) {
            console.error(err);
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    }, [selectedCompany]);

    const handleUserSelect = (user: any) => {
        setSearchResults([]);
        setSearchQuery('');
        if (!selectedUsers.find((u) => u.id === user.id)) {
            setSelectedUsers((prev) => [...prev, user]);
        }
    };

    const removeUser = (userId: number) => {
        setSelectedUsers((prev) => prev.filter((u) => u.id !== userId));
    };

    const getUserLabel = (user: any) =>
        user.displayName || user.username || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || String(user.id);

    const confirm = () => {
        if (selectedUsers.length === 0) {
            setErrorDialog(true);
            return;
        }
        setDialog(true);
    };

    const cancel = () => {
        setDialog(false);
        setSnackBar('info');
        setPageResponse('Force Logout Cancelled.');
    };

    const forceLogout = async () => {
        setDialog(false);
        const userIds = selectedUsers.map((u: any) => u.id);
        const response: any = await UserApi.forceLogoutUsers(userIds);
        if (response !== null && response !== undefined && !response.error) {
            setSnackBar('success');
            setPageResponse(`Successfully force logged out ${selectedUsers.length} user(s).`);
            setSelectedUsers([]);
        } else {
            const msg = response?.message
                ? `Force Logout Failed: ${response.message}`
                : 'Force Logout Failed.';
            setSnackBar('error');
            setPageResponse(msg);
        }
    };

    return (
        <>
            <SRCards />
            <Container>
                <SelectCompany optionHandler={onCompanySelect} />
                <br /><br />

                <TextField
                    fullWidth
                    label="Search User by Name or Email"
                    variant="outlined"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Type at least 2 characters to search..."
                    disabled={!selectedCompany || !selectedCompany.value}
                />

                {searching && (
                    <Box display="flex" justifyContent="center" style={{ marginTop: 8 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}

                {searchResults.length > 0 && (
                    <Paper style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #ccc', borderRadius: 4, marginTop: 8 }}>
                        <List>
                            {searchResults.map((user) => (
                                <ListItem key={user.id} button onClick={() => handleUserSelect(user)}>
                                    <ListItemText
                                        primary={getUserLabel(user)}
                                        secondary={user.email}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                )}

                {selectedUsers.length > 0 && (
                    <Box style={{ marginTop: 16 }}>
                        <Typography variant="body2" style={{ marginBottom: 8 }}>Selected Users:</Typography>
                        <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                            {selectedUsers.map((user) => (
                                <Chip
                                    key={user.id}
                                    label={getUserLabel(user)}
                                    onDelete={() => removeUser(user.id)}
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                <br />
                <Button
                    style={{ margin: 10 }}
                    variant="outlined"
                    onClick={confirm}
                >
                    Force Logout
                </Button>

                <Dialog
                    open={dialog}
                    onClose={() => setDialog(false)}
                    aria-labelledby="force-logout-dialog-title"
                >
                    <DialogTitle id="force-logout-dialog-title">Confirmation!</DialogTitle>
                    <DialogContent>
                        This operation will force logout {selectedUsers.length} user(s). Are you sure?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => forceLogout()}>Confirm</Button>
                        <Button onClick={() => cancel()} autoFocus>Cancel</Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={errorDialog}
                    aria-labelledby="error-dialog-title"
                >
                    <DialogContent>
                        Please select a Company and at least one user before proceeding.
                    </DialogContent>
                    <Button onClick={() => setErrorDialog(false)}>Ok</Button>
                </Dialog>

                <Snackbar
                    open={snackBar !== ''}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    autoHideDuration={6000}
                    onClose={() => setSnackBar('')}
                >
                    <Alert
                        severity={snackBar === 'error' ? 'error' : 'success'}
                        onClose={() => setSnackBar('')}
                    >
                        {pageResponse}
                    </Alert>
                </Snackbar>
            </Container>
        </>
    );
}
